import { useState, useEffect } from "react";
import FormHeader from "@/components/ui/organisms/MHeader";
import { MButton } from "@/components/ui/atoms/MButtons";
import MSelectCard from "@/components/ui/atoms/MSelectCard";
import MSpacer from "@/components/ui/atoms/MSpacer";
import { useNavigate } from "react-router-dom";
import { getMemberInfo } from "@/hooks/useMembers";
import { familyService } from "@/services/family.service";
import { useMemberPermission } from "@/hooks/useMemberPermission";

const OPTIONS = [
  { key: "yes", label: "Yes, provide access" },
  { key: "skip", label: "Skip for now" },
];

import { membersService } from "@/services/members.service";
import { useManagedMembersContext } from "@/context/ManagedMembersContext";

const SpouseAccess = () => {
  const navigate = useNavigate();
  const [spouse, setSpouse] = useState<any>(null);
  const [selected, setSelected] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const memberInfo = getMemberInfo(true);
  const { managedMembers } = useManagedMembersContext();

  // Get familyId and member form from localStorage
  let familyId = "";
  let memberPayload: any = {};
  let childNickname = "";
  const saved = localStorage.getItem("add_member_form");
  if (saved) {
    const form = JSON.parse(saved);
    familyId = memberInfo.current_family;
    memberPayload = form;
    childNickname = form.nickname || form.firstName || "the child";
  }

  const { handleGrantAccess } = useMemberPermission("", familyId);

  useEffect(() => {
    setLoading(true);
    setError(null);
    familyService
      .getSpouse()
      .then((data) => {
        setSpouse(data);
        setLoading(false);
      })
      .catch(() => {
        setError("Could not fetch spouse details");
        setLoading(false);
      });
  }, []);

  const handleDone = async () => {
    setActionLoading(true);
    setActionError(null);
    setSuccess(false);

    // Check if this is the first family member BEFORE adding
    const isFirstFamilyMember = managedMembers.length === 1;

    try {
      // Always create member first
      if (!memberPayload.current_family) {
        memberPayload.familyId = familyId;
      }
      console.log("Payload to be sent:", memberPayload);
      const created = await membersService.createMember(memberPayload);
      localStorage.removeItem("add_member_form");
      if (selected === "yes" && spouse && spouse.id && familyId) {
        await handleGrantAccess(created.id, spouse.id, true);
      }
      setSuccess(true);
      navigate("/profile/family/add-member/success", {
        state: {
          memberName: memberPayload.firstName,
          nickname: memberPayload.nickname,
          isChildOrPet: true,
          isFirstFamilyMember,
        },
      });
    } catch (err: any) {
      setActionError(err?.message || "Failed to complete action");
    } finally {
      setActionLoading(false);
    }
  };

  const spouseName =
    spouse?.nickname || spouse?.first_name || spouse?.name || "your spouse";

  return (
    <div
      className="profile-page-root flex flex-col"
      style={{ minHeight: "calc(var(--vh, 1vh) * 100 - 128px)" }}
    >
      <FormHeader
        module="Family"
        memberInfo={getMemberInfo()}
        title={`Provide Access to ${spouseName}`}
        description={`Confirm if you would you like to share ${childNickname}’s profile with your spouse?`}
        learnMoreContent={
          <>
            In India, only parents or legal guardians can have access to a
            child’s health records.
            <br />
            <br />
            Sharing access allows them to view, add, edit, and delete records
            for your child.
          </>
        }
      />
      <div className="flex-1">
        <MSpacer />
        {loading ? (
          <div>Loading spouse details...</div>
        ) : (
          <div className="flex flex-col gap-[16px]">
            {OPTIONS.map((option) => (
              <MSelectCard
                key={option.key}
                text={option.label}
                selected={selected === option.key}
                textVariant="body-base-semibold"
                onClick={() => setSelected(option.key)}
              />
            ))}
          </div>
        )}
        <MSpacer mt={40} />
      </div>
      <div className="confirm-footer">
        <MButton
          size="lg"
          variant="filled"
          className="w-full"
          disabled={!selected || actionLoading}
          onClick={handleDone}
        >
          {actionLoading ? "Saving..." : "Done"}
        </MButton>
        {actionError && (
          <div style={{ color: "red", marginTop: 8 }}>{actionError}</div>
        )}
        {success && (
          <div style={{ color: "green", marginTop: 8 }}>Action completed!</div>
        )}
      </div>
    </div>
  );
};

export default SpouseAccess;
